import { css } from "styled-system/css";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { MessageCircleOff } from "lucide-react";

const overlay = css({
  position: "fixed",
  inset: 0,
  bg: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  p: "4",
});

const panel = css({
  bg: "white",
  color: "#0f172a",
  rounded: "2xl",
  width: "100%",
  maxW: "26rem",
  p: "7",
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
  borderWidth: "1px",
  borderColor: "#e2e8f0",
  textAlign: "center",
});

/**
 * Se dispara al vencer el período de prueba sin suscripción activa. No tiene botón de cerrar
 * (×) a propósito — solo empuja a activar la suscripción o a seguir sin WhatsApp; nunca bloquea
 * el resto del dashboard. `onDismiss` marca sessionStorage para no repetirse en esta sesión.
 */
export function TrialExpiredModal({
  open,
  onDismiss,
  plan,
}: {
  open: boolean;
  onDismiss: () => void;
  /** Plan del trial (FREE/PRO/ENTERPRISE) — decide qué plan resaltar en /dashboard/subscription
   *  para que el cliente no termine viendo primero la calculadora de Enterprise y pagando un
   *  plan distinto al que venía usando en su período de prueba. */
  plan?: string;
}) {
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const highlight = plan === "ENTERPRISE" ? "enterprise" : "pro";

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={overlay}
          role="dialog"
          aria-modal
          aria-labelledby="trial-expired-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.18, ease: [0.23, 1, 0.32, 1] }}
        >
          <motion.div
            className={panel}
            initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.22, ease: [0.23, 1, 0.32, 1] }}
          >
            <div
              className={css({
                w: "12",
                h: "12",
                rounded: "full",
                bg: "rgba(99, 102, 241, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: "4",
                color: "token(colors.brand.600)",
              })}
            >
              <MessageCircleOff size={22} strokeWidth={1.75} />
            </div>

            <h2 id="trial-expired-title" className={css({ fontSize: "lg", fontWeight: "bold", mb: "2" })}>
              Tu período de prueba terminó
            </h2>
            <p className={css({ fontSize: "sm", color: "#64748b", lineHeight: "1.6", mb: "6" })}>
              Para seguir enviando confirmaciones y recordatorios por WhatsApp a tus pacientes, activá tu
              suscripción. El resto de SFTurnos sigue funcionando normalmente.
            </p>

            <button
              type="button"
              onClick={() => {
                onDismiss();
                navigate(`/dashboard/subscription?highlight=${highlight}`);
              }}
              className={css({
                width: "100%",
                px: "4",
                py: "3",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                color: "white",
                border: "none",
                cursor: "pointer",
                background: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
                transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                _active: { transform: "scale(0.98)" },
              })}
            >
              Activar suscripción
            </button>

            <button
              type="button"
              onClick={onDismiss}
              className={css({
                display: "block",
                mx: "auto",
                mt: "4",
                bg: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "xs",
                color: "#94a3b8",
                textDecoration: "underline",
                _hover: { color: "#64748b" },
              })}
            >
              Seguir sin WhatsApp por ahora
            </button>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
